import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../utils/axios";
import { toast } from "react-toastify";
import Cookies from "js-cookie";
import { trackAddToCart } from "../../helpers/gtm";

// Normalize images: converts [{url, order}] objects OR strings into plain URL strings
const normalizeImages = (images) => {
  if (!Array.isArray(images)) return [];
  return images.map(img => (typeof img === 'string' ? img : img?.url)).filter(Boolean);
};

// Helper to check auth
const isAuthenticated = () => !!Cookies.get("artote_token");

// Async thunks for cart operations
export const fetchCart = createAsyncThunk(
  "cart/fetchCart",
  async (_, { rejectWithValue, getState }) => {
    if (!isAuthenticated()) {
      const state = getState();
      const currentItems = state.cart.cartItems;
      const hydratedItems = [];
      const idsToFetch = [];
      const itemMap = {};

      // Identify items that need repair (e.g., placeholder name or 0 price)
      currentItems.forEach(item => {
        // If repair needed (name is "Product" or price 0) AND has valid ID
        if ((item.name === "Product" || item.price === 0) && (item.id || item._id)) {
          const id = item.id || item._id;
          if (!itemMap[id]) {
            idsToFetch.push(id);
            itemMap[id] = [];
          }
          itemMap[id].push(item);
        } else {
          hydratedItems.push(item);
        }
      });

      if (idsToFetch.length > 0) {
        try {
          const responses = await Promise.allSettled(
            idsToFetch.map(id => axiosInstance.get(`/product/detail/${id}`))
          );

          responses.forEach((result, index) => {
            if (result.status === 'fulfilled') {
              const resData = result.value.data;
              let productData = null;
              if (resData && resData.data) {
                productData = Array.isArray(resData.data) ? resData.data[0] : resData.data;
              } else if (resData) {
                productData = resData;
              }

              if (productData) {
                const id = idsToFetch[index];
                // Update strictly the missing info, preserve quantity/variants
                const itemsToUpdate = itemMap[id];
                itemsToUpdate.forEach(oldItem => {
                  const price = productData.price || productData.productPrice || 0;
                  const finalPrice = productData.finalPrice || productData.discountPrice || price;

                  hydratedItems.push({
                    ...oldItem,
                    name: productData.name || productData.productName,
                    price: price,
                    discountPrice: productData.discountPrice,
                    finalPrice: finalPrice,
                    images: normalizeImages(productData.images || productData.image || []),
                    slug: productData.slug,
                    category: Array.isArray(productData.category)
                      ? productData.category
                      : (productData.category ? [productData.category] : (oldItem.category || [])),
                    // Recalculate total based on new price
                    itemTotal: finalPrice * oldItem.quantity
                  });
                });
              }
            }
          });

        } catch (error) {
          console.error("Cart hydration failed", error);
        }
      } else {
        // If no repairs needed, just return original list (via isLocal logic later? 
        // No, we need to pass items back if we want to update state)
        // Actually, if we return { isLocal: true, items: [...] }, reducer needs to handle it.
        // If idsToFetch is 0, we can just return { isLocal: true } and let reducer keep state.
        return { isLocal: true };
      }

      return { isLocal: true, items: hydratedItems };
    }
    try {
      const response = await axiosInstance.get("/cart");
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch cart");
    }
  }
);

export const addToCart = createAsyncThunk(
  "cart/addToCart",
  async (item, { rejectWithValue, getState, dispatch }) => {
    const productId = item.id || item._id;
    const quantity = item.quantity || 1;

    trackAddToCart(item, quantity);

    if (!isAuthenticated()) {
      const state = getState();
      const existingItem = state.cart.cartItems.find(i => i.id === productId);

      if (existingItem) {
        toast.success("Item added to cart successfully");
        return {
          item: { ...existingItem, quantity: existingItem.quantity + quantity },
          isLocal: true,
          mode: 'update'
        };
      } else {
        if (!item.name && !item.productName) {
          // Fallback if name is missing (shouldn't happen with correct usage)
          console.warn("Product details missing for local cart", item);
        }

        // Normalize price
        const price = item.price || item.productPrice || 0;
        const discountPrice = item.discountPrice || null;
        const finalPrice = item.finalPrice;

        // Handle images array — normalize to plain URL strings
        let imageToUse = [];
        if (Array.isArray(item.images) && item.images.length > 0) {
          imageToUse = normalizeImages(item.images);
        } else if (Array.isArray(item.image) && item.image.length > 0) {
          imageToUse = normalizeImages(item.image);
        } else if (typeof item.image === 'string') {
          imageToUse = [item.image];
        }

        const newItem = {
          id: productId,
          name: item.name || item.productName || "Product",
          slug: item.slug || "",
          images: imageToUse,
          category: item.category || [],
          price: price,
          discountPrice: discountPrice,
          finalPrice: finalPrice,
          quantity: quantity,
          itemTotal: finalPrice * quantity,
          cartItemId: Date.now()
        };

        toast.success("Item added to cart successfully");
        return { item: newItem, isLocal: true, mode: 'add' };
      }
    }

    try {
      // Check if item really has ID.
      if (!productId) return rejectWithValue("Invalid product ID");

      const response = await axiosInstance.post("/cart/add", {
        productId: productId,
        quantity: quantity,
        ...(item.customization ? { customization: item.customization } : {})
      });
      toast.success(response.data.message || "Item added to cart successfully");
      dispatch(fetchCart());
      return { productId, quantity, isLocal: false };
    } catch (error) {
      const errorMsg = error.response?.data?.message || "Failed to add to cart";
      toast.error(errorMsg);
      return rejectWithValue(errorMsg);
    }
  }
);

export const updateCartQuantity = createAsyncThunk(
  "cart/updateQuantity",
  async ({ id, quantity, customizationId }, { rejectWithValue, dispatch }) => {
    if (!isAuthenticated()) {
      toast.success("Cart updated successfully");
      return { id, quantity, isLocal: true };
    }

    try {
      await axiosInstance.put(`/cart/update/${id}`, {
        productId: id,
        quantity: quantity,
        ...(customizationId ? { customizationId } : {})
      });
      toast.success("Cart updated successfully");
      dispatch(fetchCart());
      return { productId: id, quantity, isLocal: false };
    } catch (error) {
      const errorMsg = error.response?.data?.message || "Failed to update cart";
      toast.error(errorMsg);
      return rejectWithValue(errorMsg);
    }
  }
);

export const removeFromCart = createAsyncThunk(
  "cart/removeFromCart",
  async (payload, { rejectWithValue, dispatch }) => {
    // Accepts either a plain productId (existing call sites) or
    // { id, customizationId } to target one specific customized cart line.
    const { id, customizationId } = typeof payload === "object" && payload !== null
      ? payload
      : { id: payload, customizationId: undefined };

    if (!isAuthenticated()) {
      toast.success("Item removed from cart");
      return { id, isLocal: true };
    }

    try {
      const response = await axiosInstance.delete(`/cart/remove/${id}`, {
        data: { productId: id, ...(customizationId ? { customizationId } : {}) }
      });
      toast.success(response.data.message || "Item removed from cart");
      dispatch(fetchCart());
      return { id, isLocal: false };
    } catch (error) {
      const errorMsg = error.response?.data?.message || "Failed to remove from cart";
      toast.error(errorMsg);
      return rejectWithValue(errorMsg);
    }
  }
);

export const deleteAllFromCart = createAsyncThunk(
  "cart/deleteAllFromCart",
  async (_, { rejectWithValue, dispatch }) => {
    if (!isAuthenticated()) {
      toast.success("Cart cleared successfully");
      return { isLocal: true };
    }

    try {
      const response = await axiosInstance.delete("/cart/clear");
      toast.success(response.data.message || "Cart cleared successfully");
      dispatch(fetchCart());
      return { isLocal: false };
    } catch (error) {
      const errorMsg = error.response?.data?.message || "Failed to clear cart";
      toast.error(errorMsg);
      return rejectWithValue(errorMsg);
    }
  }
);

const cartSlice = createSlice({
  name: "cart",
  initialState: {
    cartItems: [],
    loading: false,
    error: null,
    totalItems: 0,
    subtotal: 0
  },
  reducers: {
    updateCartItemLocally: (state, action) => {
      const { id, quantity } = action.payload;
      const item = state.cartItems.find(item => item.id === id);
      if (item) {
        item.quantity = quantity;
        item.itemTotal = item.finalPrice * quantity;
      }
      state.totalItems = state.cartItems.reduce((acc, item) => acc + item.quantity, 0);
      state.subtotal = state.cartItems.reduce((acc, item) => acc + item.itemTotal, 0);
    },
    clearCart: (state) => {
      state.cartItems = [];
      state.totalItems = 0;
      state.subtotal = 0;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCart.fulfilled, (state, action) => {
        state.loading = false;

        if (action.payload.isLocal) {
          if (action.payload.items) {
            state.cartItems = action.payload.items;
          }
          // Re-calculate totals just in case
          state.totalItems = state.cartItems.reduce((acc, item) => acc + item.quantity, 0);
          state.subtotal = state.cartItems.reduce((acc, item) => acc + (item.finalPrice * item.quantity), 0);
          return;
        }

        // Server response
        state.cartItems = action.payload.items.map(item => ({
          id: item.product._id,
          name: item.product.name,
          slug: item.product.slug,
          images: normalizeImages(item.product.images),
          category: item.product.category,
          price: item.price,
          discountPrice: item.discountPrice,
          finalPrice: item.finalPrice,
          quantity: item.quantity,
          itemTotal: item.itemTotal,
          customization: item.customization || null
        }));
        state.totalItems = action.payload.totalItems;
        state.subtotal = action.payload.subtotal;
      })
      .addCase(fetchCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(addToCart.pending, (state) => {
        state.loading = true;
      })
      .addCase(addToCart.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.isLocal) {
          if (action.payload.mode === 'add') {
            state.cartItems.push(action.payload.item);
          } else if (action.payload.mode === 'update') {
            const { id, quantity } = action.payload.item;
            const item = state.cartItems.find(i => i.id === id);
            if (item) {
              item.quantity = quantity;
              item.itemTotal = item.finalPrice * quantity;
            }
          }
          state.totalItems = state.cartItems.reduce((acc, item) => acc + item.quantity, 0);
          state.subtotal = state.cartItems.reduce((acc, item) => acc + (item.finalPrice * item.quantity), 0);
        }
      })
      .addCase(addToCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(updateCartQuantity.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateCartQuantity.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.isLocal) {
          const { id, quantity } = action.payload;
          const item = state.cartItems.find(i => i.id === id);
          if (item) {
            item.quantity = quantity;
            item.itemTotal = item.finalPrice * item.quantity; // Fix: use updated quantity
          }
          state.totalItems = state.cartItems.reduce((acc, item) => acc + item.quantity, 0);
          state.subtotal = state.cartItems.reduce((acc, item) => acc + (item.finalPrice * item.quantity), 0);
        }
      })
      .addCase(updateCartQuantity.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(removeFromCart.pending, (state) => {
        state.loading = true;
      })
      .addCase(removeFromCart.fulfilled, (state, action) => {
        state.loading = false;
        // Check if isLocal flag exists or just fallback
        if (action.payload.isLocal || !isAuthenticated()) {
          const idToRemove = action.payload.id || action.payload;
          state.cartItems = state.cartItems.filter(item => item.id !== idToRemove);
          state.totalItems = state.cartItems.reduce((acc, item) => acc + item.quantity, 0);
          state.subtotal = state.cartItems.reduce((acc, item) => acc + (item.finalPrice * item.quantity), 0);
        }
      })
      .addCase(removeFromCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(deleteAllFromCart.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteAllFromCart.fulfilled, (state, action) => {
        state.loading = false;
        state.cartItems = [];
        state.totalItems = 0;
        state.subtotal = 0;
      })
      .addCase(deleteAllFromCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { updateCartItemLocally, clearCart } = cartSlice.actions;
export default cartSlice.reducer;
